import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Button,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { validatePlaceholders } from "../../DataStoreHelpers/dataStoreHelpers";
import { setEditQueryFormDataInput } from "../../../../../features/dataStoreReqSlice";
import CloseIcon from "@mui/icons-material/Close"; // optional
// Update path as needed

const EditInput = () => {
  const { editQueryFormData, editQueryFormDataInput } = useSelector(
    (state: any) => state.dataStoreReq
  );
  const dispatch = useDispatch();
  const placeholders = validatePlaceholders(editQueryFormData.query);
  const placeholderCount = placeholders?.placeholders?.length || 0;

  const handleInputChange = (id: string, value: any) => {
    dispatch(
      setEditQueryFormDataInput({
        [id]: value,
      })
    );
  };

  const handleArrayValueChange = (
    index: number,
    arrayIndex: number,
    newValue: string
  ) => {
    const key = `${index}-value_for_test`;
    const currentArray = editQueryFormDataInput[key] || [];
    const updatedArray = [...currentArray];
    updatedArray[arrayIndex] = newValue;

    handleInputChange(key, updatedArray);
  };

  const handleAddArrayValue = (index: number) => {
    const key = `${index}-value_for_test`;
    const currentArray = editQueryFormDataInput[key] || [];

    // If the array format is selected, initialize it with two empty values by default
    if (currentArray.length === 0) {
      handleInputChange(key, ["", ""]);
    } else {
      handleInputChange(key, [...currentArray, ""]);
    }
  };

  const handleRemoveArrayValue = (index: number, arrayIndex: number) => {
    const key = `${index}-value_for_test`;
    const currentArray = editQueryFormDataInput[key] || [];
    const updatedArray = currentArray.filter(
      (_: any, i: number) => i !== arrayIndex
    );

    // Prevent removal of the last item if there are only two items
    if (updatedArray.length >= 2) {
      handleInputChange(key, updatedArray);
    }
  };

  useEffect(() => {
    // Only run if there’s no input yet
    if (
      !editQueryFormDataInput ||
      Object.keys(editQueryFormDataInput).length === 0
    ) {
      const placeholdersMap = placeholders?.placeholders || [];

      const prefilledInputs = placeholdersMap.reduce(
        (acc: any, placeholder: any, index: any) => {
          const schema = editQueryFormData.input?.properties?.[placeholder];

          if (schema) {
            acc[`${index}-title`] = schema.title || "";
            acc[`${index}-description`] = schema.description || "";
            acc[`${index}-format`] = schema.format || "";
            acc[`${index}-order`] = schema.order || index;

            let schemaValue = schema.value_for_test || "";

            // 🚀 Correct handling of extra quotes
            if (typeof schemaValue === "string") {
              schemaValue = schemaValue.replace(/^'(.*)'$/, "$1");
            }

            if (schema.format === "array") {
              if (Array.isArray(schemaValue)) {
                acc[`${index}-value_for_test`] = schemaValue.map((val) => {
                  if (typeof val === "string") {
                    return val.replace(/^'(.*)'$/, "$1"); // remove starting/ending single quotes
                  }
                  return val;
                });
              } else {
                acc[`${index}-value_for_test`] = ["", ""];
              }
            } else {
              acc[`${index}-value_for_test`] = schemaValue;
            }
          }

          return acc;
        },
        {} as Record<string, any>
      );

      dispatch(
        setEditQueryFormDataInput({
          __replace: true,
          data: prefilledInputs,
        })
      );
    }
    // eslint-disable-next-line
  }, [editQueryFormData.query, editQueryFormDataInput]);

  return (
    <>
      {placeholderCount > 0 ? (
        <Box>
          {Array.isArray(placeholders?.placeholders) &&
            placeholders.placeholders
              ?.map((input: any, index: number) => {
                const order = parseInt(
                  editQueryFormDataInput[`${index}-order`] ?? index,
                  10
                );
                return { input, index, order };
              })
              .sort((a, b) => a.order - b.order)
              .map(({ input, index }) => (
                <div key={index}>
                  <Stack
                    flexDirection={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    mt={2}
                  >
                    <Typography>{input}</Typography>
                    <FormGroup>
                      <Stack flexDirection={"row"}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="secondary"
                              disabled
                              checked={
                                editQueryFormDataInput[`${index}-uniq`] || false
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  `${index}-uniq`,
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="UNIQ"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="secondary"
                              disabled
                              checked={
                                editQueryFormDataInput[`${index}-notMutable`] ||
                                false
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  `${index}-notMutable`,
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Not Mutable"
                        />
                      </Stack>
                    </FormGroup>
                  </Stack>

                  <Grid container spacing={2} mt={2}>
                    <Grid item xs={6}>
                      <TextField
                        id={`${index}-title`}
                        label="Title"
                        fullWidth
                        size="small"
                        color="secondary"
                        placeholder="Please enter Title"
                        slotProps={{ inputLabel: { shrink: true } }}
                        inputProps={{ maxLength: 256 }}
                        onChange={(e) =>
                          handleInputChange(`${index}-title`, e.target.value)
                        }
                        value={editQueryFormDataInput[`${index}-title`] || ""}
                        required
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Description"
                        fullWidth
                        size="small"
                        color="secondary"
                        placeholder="Please enter Description"
                        slotProps={{ inputLabel: { shrink: true } }}
                        onChange={(e) =>
                          handleInputChange(
                            `${index}-description`,
                            e.target.value
                          )
                        }
                        value={
                          editQueryFormDataInput[`${index}-description`] || ""
                        }
                        inputProps={{ maxLength: 256 }}
                        required
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mt={2}>
                    <Grid item xs={6}>
                      <Autocomplete
                        disablePortal={false}
                        options={["string", "number", "date", "array"]}
                        value={editQueryFormDataInput[`${index}-format`] || ""}
                        inputValue={
                          editQueryFormDataInput[`${index}-format`] || ""
                        }
                        onInputChange={(_: unknown, newInputValue: any) => {
                          handleInputChange(`${index}-format`, newInputValue);
                          if (newInputValue === "array") {
                            handleInputChange(`${index}-value_for_test`, [
                              "",
                              "",
                            ]);
                          } else {
                            handleInputChange(`${index}-value_for_test`, "");
                          }
                        }}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            placeholder="Please enter Format"
                            label="Format"
                            fullWidth
                            size="small"
                            color="secondary"
                            required
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        id={`${index}-order`}
                        label="Order"
                        type="number"
                        fullWidth
                        size="small"
                        color="secondary"
                        placeholder="Please enter Order"
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                          }
                        }}
                        slotProps={{ inputLabel: { shrink: true } }}
                        onChange={(e) =>
                          handleInputChange(`${index}-order`, e.target.value)
                        }
                        value={editQueryFormDataInput[`${index}-order`] || ""}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {editQueryFormDataInput[`${index}-format`] === "array" ? (
                        <>
                          <Typography fontWeight={500} mb={1}>
                            Value for test
                          </Typography>
                          {Array.isArray(
                            editQueryFormDataInput[`${index}-value_for_test`]
                          ) &&
                            editQueryFormDataInput[
                              `${index}-value_for_test`
                            ].map((val: string, arrayIndex: number) => (
                              <Grid
                                container
                                spacing={1}
                                alignItems="center"
                                key={arrayIndex}
                                mb={1}
                              >
                                <Grid item xs={9}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label={`Value ${arrayIndex + 1}`}
                                    value={val}
                                    required
                                    color="secondary"
                                    onChange={(e) =>
                                      handleArrayValueChange(
                                        index,
                                        arrayIndex,
                                        e.target.value
                                      )
                                    }
                                    inputProps={{ maxLength: 256 }}
                                  />
                                </Grid>
                                <Grid item xs={1}>
                                  <IconButton
                                    aria-label="Remove"
                                    onClick={() =>
                                      handleRemoveArrayValue(index, arrayIndex)
                                    }
                                    color="error"
                                    size="small"
                                    disabled={
                                      editQueryFormDataInput[
                                        `${index}-value_for_test`
                                      ].length <= 2
                                    }
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Grid>
                                {arrayIndex ===
                                  editQueryFormDataInput[
                                    `${index}-value_for_test`
                                  ].length -
                                  1 && (
                                    <Grid item xs={2}>
                                      <Button
                                        onClick={() => handleAddArrayValue(index)}
                                        color="secondary"
                                        variant="outlined"
                                        size="small"
                                        disabled={!val?.trim()}
                                      >
                                        +
                                      </Button>
                                    </Grid>
                                  )}
                              </Grid>
                            ))}
                          {!Array.isArray(
                            editQueryFormDataInput[`${index}-value_for_test`]
                          ) && (
                              <Grid container spacing={1} alignItems="center">
                                <Grid item xs={10}>
                                  <TextField
                                    fullWidth
                                    color="secondary"
                                    size="small"
                                    label="Value 1"
                                    required
                                    onChange={(e) =>
                                      handleInputChange(
                                        `${index}-value_for_test`,
                                        [e.target.value]
                                      )
                                    }
                                    inputProps={{ maxLength: 256 }}
                                  />
                                </Grid>
                                <Grid item xs={2}>
                                  <Button
                                    onClick={() => handleAddArrayValue(index)}
                                    color="secondary"
                                    variant="outlined"
                                    size="small"
                                  >
                                    +
                                  </Button>
                                </Grid>
                              </Grid>
                            )}
                        </>
                      ) : (
                        <TextField
                          id={`${index}-value_for_test`}
                          label="Value for test"
                          fullWidth
                          size="small"
                          color="secondary"
                          placeholder="Please enter Value"
                          slotProps={{ inputLabel: { shrink: true } }}
                          onChange={(e) =>
                            handleInputChange(
                              `${index}-value_for_test`,
                              e.target.value
                            )
                          }
                          disabled={!editQueryFormDataInput[`${index}-format`]}
                          value={
                            editQueryFormDataInput[`${index}-value_for_test`] ||
                            ""
                          }
                          required
                          inputProps={{ maxLength: 256 }}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "12px" }}
                        color="#00000099"
                      >
                        All fields are mandatory.
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
              ))}
        </Box>
      ) : (
        <Box>
          <Typography
            variant="body2"
            sx={{ fontSize: "12px" }}
            color="#00000099"
            margin={2}
          >
            No input fields available.
          </Typography>
        </Box>
      )}
    </>
  );
};

export default EditInput;
